import axios from "axios";
import { getToken, removeToken, saveToken } from "../utils/auth-storage";
import { API_CONFIG } from "../config/api";

// Créer une instance axios avec des timeouts plus longs et une configuration robuste
const axiosInstance = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: {
    "Content-Type": "application/json",
  },
});

// Variables pour gérer les requêtes en échec
let pendingAuthRequests = 0;

// Intercepteur pour automatiquement ajouter le token aux requêtes
axiosInstance.interceptors.request.use(
  (config) => {
    // Ne pas bloquer les requêtes d'authentification
    const isAuthRequest =
      config.url === "/auth/login" ||
      config.url === "/auth/register" ||
      config.url?.includes("/auth/forgot-password");

    if (isAuthRequest) {
      return config;
    }

    // Utiliser notre système amélioré de gestion de token
    const token = getToken();

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }


    return config;
  },
  (error) => {
    console.error("Request error in interceptor:", error);
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs de réponse
axiosInstance.interceptors.response.use(
  (response) => {
    // Si c'est un rafraîchissement de token réussi, mettre à jour l'expiration
    if (response?.config?.url === "/auth/refresh") {
      if (response.data?.accessToken) {
        saveToken(response.data.accessToken);
      }
    }
    return response;
  },
  async (error) => {
    // Ne pas répéter les requêtes qui échouent plusieurs fois
    if (error.config?.__retryCount >= 1) {
      return Promise.reject(error);
    }

    const status = error?.response?.status;
    const config = error?.config;

    // Ignorer les erreurs 401 pour les requêtes Azure Static Web Apps Auth
    // if (status === 401 && config?.url?.includes('.auth/sessions/whoami')) {
    //   console.log('Ignoring Azure SWA auth error - not configured for local development');
    //   // Return a resolved promise to prevent the error from propagating
    //   return Promise.resolve({
    //     data: null,
    //     status: 200,
    //     statusText: "OK (ignoring Azure SWA auth error)",
    //     headers: {},
    //     config,
    //   });
    // }

    // Gestion spécifique des erreurs 401 (non authentifié)
    if (status === 401 && config?.url !== "/auth/login") {
      const isAuthMeRequest = config?.url?.endsWith("/auth/me");

      // Pour /auth/me, permettre une seule erreur 401 avant déconnexion
      if (isAuthMeRequest) {
        pendingAuthRequests++;

        if (pendingAuthRequests > 3) {
          console.warn("Multiple auth check failures detected, logging out");
          removeToken();
          pendingAuthRequests = 0;
          return Promise.reject(error);
        }

        // Laisser passer l'erreur pour que RefineAuthWrapper puisse la traiter
        return Promise.reject(error);
      }

      // Pour les autres requêtes, renvoyer une réponse vide
      if (process.env.NODE_ENV === "development") {

        return Promise.resolve({
          data: { data: [] },
          status: 200,
          statusText: "OK (mocked)",
          headers: {},
          config,
        });
      }
    }

    // Pour les erreurs 404 sur les requêtes bloquées, supprimer les erreurs
    if (status === 404 && config?.url?.startsWith("data:text/plain")) {
      return Promise.resolve({
        data: { data: [] },
        status: 200,
        statusText: "OK (blocked)",
        headers: {},
        config,
      });
    }

    // Autres erreurs: laisser passer normalement
    if (error.response) {
      const { url } = error.config;
      const { status, data } = error.response;

      console.error(`API Error ${status} for ${url}:`, data);

      if (status === 401 || status === 403) {
        if (window.location.pathname !== "/login") {
          localStorage.removeItem("accessToken");

          // Délai pour éviter la boucle de redirection
          setTimeout(() => {
            window.location.href = "/login";
          }, 100);
        }
      }
    } else if (error.request) {
      console.error("No response received:", error.request);
    } else {
      console.error("Error setting up request:", error.message);
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
